import { TelegramAPISettings } from "../telegram_api_settings.js";

export type AvatarGradient = { from: string; to: string };

// Who a Telegram-style avatar belongs to: the gradient seed, the initials and,
// when known, the photo. Other players only ever carry a name.
export interface TelegramAvatarIdentity
{
  seed: number | string;
  first_name: string;
  last_name: string;
  photo_url: string | null;
}

export class TelegramAvatar
{
  private constructor() {}

  private static readonly INITIALS_FONT = "Rubik, 'Arial Black', Arial, sans-serif";

  public static currentIdentity(): TelegramAvatarIdentity
  {
    const tg_user = TelegramAPISettings.getUser();
    const first_name = tg_user ? tg_user.first_name ?? "" : localStorage.getItem( "player_nickname" ) ?? "";
    return {
      seed: tg_user?.id ?? first_name,
      first_name,
      last_name: tg_user?.last_name ?? "",
      photo_url: tg_user?.photo_url ?? null,
    };
  }

  public static identityFromName( name: string ): TelegramAvatarIdentity
  {
    return { seed: name, first_name: name, last_name: "", photo_url: null };
  }

  // Gradient circle with the initials, centred on the current origin.
  public static drawInitials( ctx: CanvasRenderingContext2D, identity: TelegramAvatarIdentity, radius: number ): void
  {
    const gradient = TelegramAvatar.getGradient( identity.seed );
    const fill = ctx.createLinearGradient( 0, -radius, 0, radius );
    fill.addColorStop( 0, gradient.from );
    fill.addColorStop( 1, gradient.to );

    ctx.beginPath();
    ctx.arc( 0, 0, radius, 0, Math.PI * 2 );
    ctx.fillStyle = fill;
    ctx.fill();

    ctx.fillStyle = "#FFFFFF";
    ctx.font = `900 ${ radius * 0.98 }px ${ TelegramAvatar.INITIALS_FONT }`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText( TelegramAvatar.getInitials( identity.first_name, identity.last_name ), 0, radius * 0.04 );
  }

  private static readonly GRADIENTS: AvatarGradient[] = [
    { from: "#FF885E", to: "#FF516A" },
    { from: "#FFCD6A", to: "#FFA85C" },
    { from: "#82B1FF", to: "#665FFF" },
    { from: "#A0DE7E", to: "#54CB68" },
    { from: "#53EDD6", to: "#28C9B7" },
    { from: "#72D5FD", to: "#2A9EF1" },
    { from: "#E0A2F3", to: "#D669ED" },
  ];

  private static readonly PHOTO_TEXTURE_SIZE = 256;

  private static readonly photo_cache = new Map<string, Promise<ImageBitmap>>();

  public static getGradient( seed: number | string ): AvatarGradient
  {
    const numeric = Number( seed );
    const key = Number.isSafeInteger( numeric )
      ? Math.abs( numeric )
      : TelegramAvatar.hashString( String( seed ) );

    return TelegramAvatar.GRADIENTS[ key % TelegramAvatar.GRADIENTS.length ];
  }

  public static getInitials( first_name = "", last_name = "" ): string
  {
    const words = `${ first_name } ${ last_name }`.trim().split( /\s+/ ).filter( Boolean );
    if ( words.length === 0 )
      return "?";

    const first_char = ( word: string ) => Array.from( word )[0].toUpperCase();
    if ( words.length === 1 )
      return first_char( words[0] );

    return first_char( words[0] ) + first_char( words[ words.length - 1 ] );
  }

  public static loadPhoto( url: string ): Promise<ImageBitmap>
  {
    const cached = TelegramAvatar.photo_cache.get( url );
    if ( cached ) return cached;

    const promise = TelegramAvatar.loadImage( url, true )
      .catch( () => TelegramAvatar.loadImage( url, false ) )
      .then( img => TelegramAvatar.rasterizeCircle( img ) )
      .catch( e =>
      {
        TelegramAvatar.photo_cache.delete( url );
        console.warn( "[TelegramAvatar] Failed to load photo:", e );
        throw e;
      } );

    TelegramAvatar.photo_cache.set( url, promise );
    return promise;
  }

  private static loadImage( url: string, cross_origin: boolean ): Promise<HTMLImageElement>
  {
    return new Promise( ( resolve, reject ) =>
    {
      const img = new Image();
      if ( cross_origin )
        img.crossOrigin = "anonymous";
      img.onload = () => resolve( img );
      img.onerror = () => reject( new Error( `Failed to load ${ url }` ) );
      img.src = url;
    } );
  }

  private static rasterizeCircle( img: HTMLImageElement ): Promise<ImageBitmap>
  {
    const size = TelegramAvatar.PHOTO_TEXTURE_SIZE;
    const canvas = document.createElement( "canvas" );
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext( "2d" )!;

    ctx.beginPath();
    ctx.arc( size / 2, size / 2, size / 2, 0, Math.PI * 2 );
    ctx.clip();

    const src_w = img.naturalWidth || size;
    const src_h = img.naturalHeight || size;
    const scale = Math.max( size / src_w, size / src_h );
    const w = src_w * scale;
    const h = src_h * scale;
    ctx.drawImage( img, ( size - w ) / 2, ( size - h ) / 2, w, h );

    return createImageBitmap( canvas );
  }

  private static hashString( value: string ): number
  {
    let hash = 0;
    for ( let i = 0; i < value.length; i++ )
      hash = ( hash * 31 + value.charCodeAt( i ) ) | 0;
    return Math.abs( hash );
  }
}
